import { ReactNode } from 'react';

import { evaluateJsonLogicRule, isJsonLogicRuleEmpty } from '@/shared/components/filters/jsonLogic';
import { WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';
import { corporateLogo } from '@/utilities';

export type PipelineItemResourceType = 'investment' | 'memo' | 'folder' | 'dashboard' | 'list';

export type PipelineItemResourceCollection = 'investments' | 'memos' | 'folders' | 'dashboards' | 'lists';

export type PipelineItemDefinition = {
    collection: PipelineItemResourceCollection;
    description: string;
    icon: (color?: string, size?: number | string) => ReactNode;
    label: string;
    pluralLabel: string;
    type: PipelineItemResourceType;
};

export const PIPELINE_ITEM_DEFINITIONS: PipelineItemDefinition[] = [
    {
        collection: 'investments',
        description: 'Track companies and investment opportunities through this pipeline.',
        icon: (color, size = '1rem') => <WorkspaceResourceGlyph color={color} resource='investment' size={size} />,
        label: 'Investment',
        pluralLabel: 'Investments',
        type: 'investment',
    },
    {
        collection: 'folders',
        description: 'Track workspace folders that collect related diligence resources.',
        icon: (color, size = '1rem') => <WorkspaceResourceGlyph color={color} resource='folder' size={size} />,
        label: 'Folder',
        pluralLabel: 'Folders',
        type: 'folder',
    },
    {
        collection: 'dashboards',
        description: 'Track dashboards and their reporting workflows through this pipeline.',
        icon: (color, size = '1rem') => <WorkspaceResourceGlyph color={color} resource='dashboard' size={size} />,
        label: 'Dashboard',
        pluralLabel: 'Dashboards',
        type: 'dashboard',
    },
    {
        collection: 'lists',
        description: 'Track relationship lists and their operating status through this pipeline.',
        icon: (color, size = '1rem') => <WorkspaceResourceGlyph color={color} resource='list' size={size} />,
        label: 'List',
        pluralLabel: 'Lists',
        type: 'list',
    },
    {
        collection: 'memos',
        description: 'Track memos, theses, and research notes through this pipeline.',
        icon: (color, size = '1rem') => <WorkspaceResourceGlyph color={color} resource='memo' size={size} />,
        label: 'Memo',
        pluralLabel: 'Memos',
        type: 'memo',
    },
];

const RELATIONS_PIPELINE_ITEM_COLLECTIONS = new Set<PipelineItemResourceCollection>(['dashboards', 'lists']);

export const getAvailablePipelineItemDefinitions = (relationsEnabled: boolean): PipelineItemDefinition[] =>
    PIPELINE_ITEM_DEFINITIONS.filter(
        (definition) => relationsEnabled || !RELATIONS_PIPELINE_ITEM_COLLECTIONS.has(definition.collection),
    );

export const isRelationsPipelineItemCollection = (collection: PipelineItemResourceCollection): boolean =>
    RELATIONS_PIPELINE_ITEM_COLLECTIONS.has(collection);

export const PIPELINE_ITEM_DEFINITION_BY_TYPE = PIPELINE_ITEM_DEFINITIONS.reduce(
    (accumulator, definition) => {
        accumulator[definition.type] = definition;
        return accumulator;
    },
    {} as Record<PipelineItemResourceType, PipelineItemDefinition>,
);

export const PIPELINE_ITEM_DEFINITION_BY_COLLECTION = PIPELINE_ITEM_DEFINITIONS.reduce(
    (accumulator, definition) => {
        accumulator[definition.collection] = definition;
        return accumulator;
    },
    {} as Record<PipelineItemResourceCollection, PipelineItemDefinition>,
);

export const getPipelineItemResourceType = (item: any): PipelineItemResourceType => {
    const taggedType = item?.pipelineResourceType as PipelineItemResourceType | undefined;

    if (taggedType && PIPELINE_ITEM_DEFINITION_BY_TYPE[taggedType]) {
        return taggedType;
    }

    if (item?.tickerSymbol || item?.editedName) {return 'investment';}
    if (item?.body !== undefined || item?.title !== undefined) {return 'memo';}
    if (item?.views !== undefined || item?.autopopulateColumns !== undefined) {return 'list';}
    if (item?.reports !== undefined || item?.publiclyVisible !== undefined) {return 'dashboard';}
    if (
        item?.investments !== undefined ||
        item?.memos !== undefined ||
        item?.pipelines !== undefined ||
        item?.dashboards !== undefined ||
        item?.lists !== undefined
    ) {
        return 'folder';
    }

    return 'investment';
};

export const tagPipelineItem = (item: any, type: PipelineItemResourceType) => ({
    ...item,
    pipelineResourceType: type,
});

export const normalizePipelineColumnCards = (column: any) =>
    PIPELINE_ITEM_DEFINITIONS.flatMap((definition) =>
        ((column?.[definition.collection] ?? []) as any[]).map((item) => tagPipelineItem(item, definition.type)),
    );

export const getPipelineItemDefinition = (itemOrType: any) =>
    PIPELINE_ITEM_DEFINITION_BY_TYPE[
        typeof itemOrType === 'string'
            ? (itemOrType as PipelineItemResourceType)
            : getPipelineItemResourceType(itemOrType)
    ];

export const getPipelineItemKey = (item: any) => `${getPipelineItemResourceType(item)}:${item?.id}`;

export interface PipelineItemSnapshot<TCard = any> {
    card: TCard;
    columnId: string | number;
    index: number;
    key: string;
}

export const capturePipelineItemSnapshots = <TCard, TColumn extends { cards?: TCard[]; id: string | number }>(
    columns: TColumn[],
    itemKeys: ReadonlySet<string>,
): PipelineItemSnapshot<TCard>[] =>
    columns.flatMap((column) =>
        (column.cards ?? []).flatMap((card, index) => {
            const key = getPipelineItemKey(card);

            return itemKeys.has(key) ? [{ card, columnId: column.id, index, key }] : [];
        }),
    );

export const restorePipelineItemSnapshots = <TCard, TColumn extends { cards?: TCard[]; id: string | number }>(
    columns: TColumn[],
    snapshots: PipelineItemSnapshot<TCard>[],
): TColumn[] => {
    if (!snapshots.length) {
        return columns;
    }

    const snapshotKeys = new Set(snapshots.map((snapshot) => snapshot.key));
    const snapshotsByColumn = snapshots.reduce<Map<string, PipelineItemSnapshot<TCard>[]>>((grouped, snapshot) => {
        const columnId = String(snapshot.columnId);
        const columnSnapshots = grouped.get(columnId) ?? [];
        columnSnapshots.push(snapshot);
        grouped.set(columnId, columnSnapshots);
        return grouped;
    }, new Map());

    return columns.map((column) => {
        const columnSnapshots = snapshotsByColumn.get(String(column.id));
        const cards = (column.cards ?? []).filter((card) => !snapshotKeys.has(getPipelineItemKey(card)));

        if (!columnSnapshots?.length) {
            return { ...column, cards };
        }

        columnSnapshots
            .slice()
            .sort((left, right) => left.index - right.index)
            .forEach((snapshot) => cards.splice(Math.min(snapshot.index, cards.length), 0, snapshot.card));

        return { ...column, cards };
    });
};

export const getPipelineItemTitle = (item: any) => {
    const type = getPipelineItemResourceType(item);

    if (type === 'investment') {return item?.editedName || item?.name || 'Untitled Investment';}
    if (type === 'memo') {return item?.title || 'Untitled Memo';}
    if (type === 'folder') {return item?.name || 'Untitled Folder';}
    if (type === 'dashboard') {return item?.name || 'Untitled Dashboard';}
    if (type === 'list') {return item?.name || 'Untitled List';}

    return item?.name || item?.title || 'Untitled Resource';
};

export const getPipelineItemDescription = (item: any) => {
    const type = getPipelineItemResourceType(item);

    if (type === 'investment') {return item?.type || item?.sector || item?.industry;}

    return item?.description || item?.visibility || item?.type;
};

export const getPipelineItemPath = (item: any) => {
    const type = getPipelineItemResourceType(item);

    if (type === 'investment') {return `/investments/${item?.id}`;}
    if (type === 'memo') {return `/memos/${item?.id}`;}
    if (type === 'folder') {return `/folders/${item?.id}`;}
    if (type === 'dashboard') {return `/dashboards/${item?.id}`;}
    if (type === 'list') {return `/lists/${item?.id}`;}

    return '/pipelines';
};

export const getPipelineItemAvatarProps = (item: any, color?: string) => {
    const definition = getPipelineItemDefinition(item);
    const type = getPipelineItemResourceType(item);

    return {
        icon: definition.icon(color),
        src: type === 'investment' && item?.website ? corporateLogo(item.website) : undefined,
    };
};

export const getPipelineItemFilterData = (item: any, column?: any) => {
    const type = getPipelineItemResourceType(item);
    const definition = getPipelineItemDefinition(type);

    return {
        createdAt: item?.createdAt,
        description: getPipelineItemDescription(item),
        id: item?.id,
        resourceLabel: definition.label,
        resourceType: type,
        stageId: column?.id,
        stageName: column?.name,
        title: getPipelineItemTitle(item),
        updatedAt: item?.updatedAt,
        visibility: item?.visibility ?? item?.type,
    };
};

export const isPipelineJsonLogicRuleEmpty = isJsonLogicRuleEmpty;
export const evaluatePipelineJsonLogicRule = evaluateJsonLogicRule;

export const filterPipelineColumnCards = (column: any, jsonLogicRule?: any) => {
    const cards = column?.cards ?? normalizePipelineColumnCards(column);

    if (isPipelineJsonLogicRuleEmpty(jsonLogicRule)) {
        return cards;
    }

    return cards.filter((card: any) => evaluateJsonLogicRule(jsonLogicRule, getPipelineItemFilterData(card, column)));
};
